import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {EnergyBalanceResponse} from '../../../../../api/models/energy-balance-response';
import {Chart, ChartData, ChartOptions, type LegendItem, registerables} from 'chart.js';
import {BaseChartComponent} from '../base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {de} from 'date-fns/locale';
import 'chartjs-adapter-date-fns';
import {MatIconModule} from '@angular/material/icon';
import { environment } from '../../../../../environments/environment';

// Register Chart.js components
Chart.register(...registerables);

@Component({
  selector: 'app-monthly-balance-chart',
  templateUrl: '../chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective, MatIconModule],
})
export class MonthlyBalanceChartComponent extends BaseChartComponent implements OnChanges {
  @Input() data!: EnergyBalanceResponse[];
  @Input() isMobile = false;
  @Input() endDate!: Date;
  @Input() startDate!: Date;
  useCorrectedValues = true;
  private readonly useCompactTooltip = typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;

  barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'day',
          tooltipFormat: 'dd.MM.',
          displayFormats: !this.isMobile ? {day: 'dd.MM.'} : {day: 'd'},
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 90,
          minRotation: 90,
          callback: (value) => {
            const date = new Date(value);
            return this.isMobile
              ? date.getDate()
              : date.toLocaleDateString(environment.locale, {
                  day: '2-digit',
                  month: '2-digit',
                  timeZone: this.timezone,
                });
          },
        },
        border: {
          display: false,
        },
      },
      y: {
        display: true,
        stacked: true,
        grid: {
          display: true,
        },
        ticks: {
          //stepSize: 20,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels(chart: Chart): LegendItem[] {
            function customText(i: number) {
              if (i == 0) {
                //set labels manually for the two columns
                return 'Gesamt-Produktion';
              } else {
                return 'Gesamt-Verbrauch';
              }
            }

            return chart.data.datasets
              .map((dataset, i) => ({
                text: customText(i), // overwrite the textes for the legend labels
                fillStyle: dataset.backgroundColor as string,
                strokeStyle: dataset.borderColor as string,
                lineWidth: 0,
                boxWidth: 30,
                hidden: false,
                datasetIndex: i,
              }))
              .filter((_, i) => i % 3 === 0); //dont show all 4 labels, but only two for the two columsn
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: this.useCompactTooltip ? 'nearest' : 'index',
        intersect: false,
        position: 'nearest',
        backgroundColor: 'rgba(0, 0, 0, 0.38)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.25)',
        borderWidth: 1,
        padding: this.useCompactTooltip ? 6 : 10,
        bodySpacing: this.useCompactTooltip ? 3 : 6,
        boxPadding: this.useCompactTooltip ? 2 : 4,
        titleFont: {
          size: this.useCompactTooltip ? 10 : 12,
        },
        bodyFont: {
          size: this.useCompactTooltip ? 10 : 12,
        },
        caretPadding: this.useCompactTooltip ? 8 : 12,
        displayColors: !this.useCompactTooltip,
        callbacks: {
          label: (context) => context.dataset.label + ': ' + context.formattedValue + ' kWh',
        },
      },
    },
  };

  constructor() {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Monthly Balance Chart received change', changes);
    if (changes['data']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.chartOptions.set(this.barChartOptions);
    this.chartData.set(this.getChartData());
    //details
    const aggregatedRow = super.getAggregatedBalanceDataRow(this.data, this.useCorrectedValues);
    const dailyData = this.data.map((data) => super.getBalanceDataRow(data, this.useCorrectedValues));
    this.balanceData = [aggregatedRow, ...dailyData];
    //independence
    this.independenceData = [super.getIndependenceDataRow(this.getAggregatedAverageResponse(this.data, this.useCorrectedValues), this.useCorrectedValues)];
    this.chart?.update();
  }

  getChartData(): ChartData<'bar'> {
    // Helper to format date as 2025-07-01T00:00:00.000+02:00
    function formatDateWithOffset(date: Date): string {
      const pad = (n: number) => n.toString().padStart(2, '0');
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      const ms = date.getMilliseconds().toString().padStart(3, '0');
      // Get timezone offset in minutes
      const offset = date.getTimezoneOffset();
      const absOffset = Math.abs(offset);
      const offsetHours = pad(Math.floor(absOffset / 60));
      const offsetMinutes = pad(absOffset % 60);
      const sign = offset <= 0 ? '+' : '-';
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${sign}${offsetHours}:${offsetMinutes}`;
    }

    // Generate all dates between startDate and endDate (inclusive)
    const dateLabels: string[] = [];
    if (!this.startDate || !this.endDate) {
      return {labels: [], datasets: []};
    }
    let current = new Date(this.startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(this.endDate);
    end.setHours(0, 0, 0, 0);
    while (current <= end) {
      dateLabels.push(formatDateWithOffset(current));
      current = new Date(current.getTime() + 24 * 60 * 60 * 1000); // add 1 day
    }

    // Map data by date (ignoring time)
    const dataMap = new Map<string, EnergyBalanceResponse>();
    if (this.data) {
      this.data.forEach((data) => {
        if (data.date) {
          const d = new Date(data.date);
          d.setHours(0, 0, 0, 0);
          dataMap.set(formatDateWithOffset(d), data);
        }
      });
    }

    const fromBatteryData: number[] = [];
    const fromGridData: number[] = [];
    const fromPVData: number[] = [];
    const toPVData: number[] = [];
    const toBatteryData: number[] = [];
    const toGridData: number[] = [];

    dateLabels.forEach((label) => {
      const data = dataMap.get(label);
      if (!data || data.dataError) {
        // If data is missing or has error, push 0s
        fromBatteryData.push(0);
        fromGridData.push(0);
        fromPVData.push(0);
        toPVData.push(0);
        toBatteryData.push(0);
        toGridData.push(0);
      } else {
        const selfConsumptionC = this.getSelfConsumptionFromConsumption(data, this.useCorrectedValues);
        const selfConsumptionP = this.getSelfConsumptionFromProduction(data, this.useCorrectedValues);
        const fromBattery = this.getEnergyFromBattery(data, this.useCorrectedValues);
        const fromGrid = data.energyFromGrid ?? 0;
        const toBattery = data.energyToBattery ?? 0;
        const toGrid = this.getEnergyToGrid(data, this.useCorrectedValues);

        // First bar (Verbrauch)
        fromBatteryData.push(-fromBattery);
        fromGridData.push(-fromGrid);
        fromPVData.push(selfConsumptionC);

        // Second bar (Produktion)
        toPVData.push(selfConsumptionP);
        toBatteryData.push(toBattery);
        toGridData.push(toGrid);
      }
    });

    return {
      labels: dateLabels,
      datasets: [
        {
          label: 'Eigenverbrauch',
          data: toPVData,
          backgroundColor: '#FBBA00',
          stack: 'production',
        },
        {
          label: 'Zur Batterie',
          data: toBatteryData,
          backgroundColor: '#FBBA00',
          stack: 'production',
        },
        {
          label: 'In Verkauf',
          data: toGridData,
          backgroundColor: '#FBBA00',
          stack: 'production',
        },
        {
          label: 'Aus Batterie',
          data: fromBatteryData,
          backgroundColor: '#00B6EE',
          stack: 'consumption',
        },
        {
          label: 'Aus Einkauf',
          data: fromGridData,
          backgroundColor: '#00B6EE',
          stack: 'consumption',
        },
        {
          label: 'Eigenverbrauch',
          data: fromPVData,
          backgroundColor: '#00B6EE',
          stack: 'consumption',
        },
      ],
    };
  }
}
